import { useInfiniteQuery } from "@tanstack/react-query";
import { creativeService } from "../api/services/creativeService";
import type {
  Creative,
  CreativeFilters,
  CreativesResponse,
} from "../schemas/creativeSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";

/**
 * The signed-in brand's own creatives.
 *
 * This calls the ordinary creatives query — there is no brand-specific
 * endpoint. The backend pins the owner from the auth context (applyBrandScope),
 * the same way it pins the tenant, so a brand only ever gets its own rows.
 *
 * Read-only by design — a brand holds `creatives.view` and nothing else, so
 * unlike useCreativesInfinite this exposes no mutations. `adGroupId` is
 * omitted because ad groups are a tenant-side grouping that means nothing to
 * a brand.
 */
export type BrandCreativesInfiniteFilters = Omit<
  CreativeFilters,
  "page" | "pageSize" | "adGroupId"
> & {
  pageSize?: number;
};

export const useBrandCreativesInfinite = (
  filters: BrandCreativesInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<CreativesResponse, Error>({
    queryKey: ["brand-creatives", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      creativeService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const creatives: Creative[] =
    query.data?.pages.flatMap((p) => p.creatives) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    creatives,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
